import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useIntl } from 'umi';
import {
  Button,
  Checkbox,
  Form,
  Modal,
  Radio,
} from 'tea-component';
import RangePicker from '@/pages/home/components/RangePicker';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import InputNumber from '@/pages/intelligence/components/Form/components/common/InputNumber';
import CascaderMetricSelect from '@intelligence/components/CascaderMetricSelect';
import { downloadAction } from '@intelligence/components/DownloadCommon/DownloadPage';
import { MAX_ROWS_LIMIT } from '@intelligence/components/DownloadCommon/DownloadPage/utils';
import {
  buildRobloxGameRankDownloadParams,
  getRobloxDownloadDefaultDateRange,
} from '../../utils/buildRobloxGameRankDownloadParams';
import {
  buildRobloxRankCascaderOptions,
  cascaderValuesToSources,
  sourcesToCascaderValues,
} from '../../utils/robloxRankCascaderOptions';
import { reportRobloxGameRankDownloadLog } from '../../config/pageLogConfig';

import type { TableSelectionType } from '@intelligence/components/TableComponents/components/TableCheckbox';
import type { IMainGenre } from '@intelligence/types/common';
import type { IRobloxGameRankFilterValues } from '../../types';
import type { IRobloxGameRankDownloadFormValues } from './types';
import type { TRobloxRankCategory } from '../../config/robloxRankSourceConfig';

import './index.scss';
import '@intelligence/components/ExportTable/components/DownloadSettingModal/index.scss';

const DOWNLOAD_TYPE_OPTIONS = [
  {
    value: 'game',
    label: 'dbi_download_type_by_game',
    tip: 'dbi_download_type_by_game_tip',
  },
  {
    value: 'time',
    label: 'dbi_download_type_by_time',
    tip: 'dbi_download_type_by_time_tip',
  },
] as const;

interface IRobloxGameRankDownloadProps {
  filterValues: IRobloxGameRankFilterValues;
  rankCategory: TRobloxRankCategory;
  rankSource: string;
  tableSelection: TableSelectionType[];
  regionOptions?: IRecord[];
  total?: number;
  robloxGenreOptions?: IMainGenre[];
}

const RobloxGameRankDownload: React.FC<IRobloxGameRankDownloadProps> = ({
  filterValues,
  rankCategory,
  rankSource,
  tableSelection,
  regionOptions = [],
  total = MAX_ROWS_LIMIT,
  robloxGenreOptions = [],
}) => {
  const { formatMessage, locale } = useIntl();
  const [visible, setVisible] = useState(false);
  const [isBlur, setIsBlur] = useState(false);
  const [formValues, setFormValues] = useState<IRobloxGameRankDownloadFormValues>(() => ({
    gameInfo: {
      device_show: false,
      genre_show: false,
    },
    sources: [rankSource],
    dateRange: getRobloxDownloadDefaultDateRange(filterValues),
    data_method: 'selectTop',
    data_amount: 10,
    select_top_type: 'time',
    file_format: 'xlsx',
    game_ids: [],
  }));

  const selectedGameIds = useMemo(() => {
    return tableSelection
      .map((item) => String(item?.gameId || ''))
      .filter(Boolean);
  }, [tableSelection]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setFormValues({
      gameInfo: {
        device_show: false,
        genre_show: false,
      },
      sources: [rankSource],
      dateRange: getRobloxDownloadDefaultDateRange(filterValues),
      data_method: 'selectTop',
      data_amount: 10,
      select_top_type: 'time',
      file_format: 'xlsx',
      game_ids: selectedGameIds,
    });
    setIsBlur(false);
  }, [visible, rankSource, filterValues, selectedGameIds]);

  const handleConfirm = (): void => {
    if (formValues.data_method === 'selectTop' && formValues.data_amount < 1) {
      setIsBlur(true);
      return;
    }

    if (!formValues.sources.length) {
      return;
    }

    const downLoadParams = buildRobloxGameRankDownloadParams({
      filterValues,
      formValues: {
        ...formValues,
        game_ids: formValues.data_method === 'checkedLines' ? selectedGameIds : [],
      },
      regionOptions,
      robloxGenreOptions,
    });

    downloadAction({
      downloadKey: 'robloxGameRank',
      downLoadParams,
    });
    reportRobloxGameRankDownloadLog(rankCategory);
    setVisible(false);
  };

  const datePickerValue = useMemo(() => {
    const [startDate, endDate] = formValues.dateRange;
    return [moment(startDate), moment(endDate || startDate)];
  }, [formValues.dateRange]);

  const rankCascaderOptions = useMemo(() => buildRobloxRankCascaderOptions(), []);

  const rankCascaderValue = useMemo(
    () => sourcesToCascaderValues(formValues.sources),
    [formValues.sources],
  );

  const rankCascaderLanguage = useMemo(
    () => ({
      en: {
        region_title: formatMessage({ id: 'dbi_roblox_download_classification' }),
        market_title: formatMessage({ id: 'dbi_roblox_rank_list' }),
        search: formatMessage({ id: 'dbi_roblox_download_search' }),
      },
      cn: {
        region_title: formatMessage({ id: 'dbi_roblox_download_classification' }),
        market_title: formatMessage({ id: 'dbi_roblox_rank_list' }),
        search: formatMessage({ id: 'dbi_roblox_download_search' }),
      },
    }),
    [formatMessage],
  );

  const resolveRankSecondLevelTitle = useCallback(
    (_selectedRegionItem, _locale) => formatMessage({ id: 'dbi_roblox_rank_list' }),
    [formatMessage],
  );

  return (
    <>
      <Button
        icon="download"
        className="ns-btn--icon trend-justify-btn dbi-roblox-rank-download-btn"
        onClick={() => setVisible(true)}
      />

      <Modal
        visible={visible}
        size={660}
        caption={formatMessage({ id: 'dbi_download_settings' })}
        className="dbi-roblox-rank-download-modal dbi-download-type"
        onClose={() => setVisible(false)}
      >
        <Modal.Body>
          <Form className="dbi-download-form teaui-form-alignment dbi-roblox-rank-download-form pc-console-topCharts">
            <Form.Item
              label={formatMessage({ id: 'dbi_roblox_download_game_info' })}
              showStatusIcon={false}
            >
              <Checkbox.Group
                value={[
                  ...(formValues.gameInfo.device_show ? ['device_show'] : []),
                  ...(formValues.gameInfo.genre_show ? ['genre_show'] : []),
                ]}
                onChange={(checkedValues) => {
                  setFormValues((prev) => ({
                    ...prev,
                    gameInfo: {
                      device_show: checkedValues.includes('device_show'),
                      genre_show: checkedValues.includes('genre_show'),
                    },
                  }));
                }}
              >
                <Checkbox name="device_show">
                  {formatMessage({ id: 'dbi_roblox_device' })}
                </Checkbox>
                <Checkbox name="genre_show">
                  {formatMessage({ id: 'dbi_roblox_genre' })}
                </Checkbox>
              </Checkbox.Group>
            </Form.Item>

            <Form.Item
              className="form-item--align-middle"
              label={formatMessage({ id: 'dbi_storeRank' })}
              showStatusIcon={false}
            >
              <CascaderMetricSelect
                locale={locale}
                className="dbi-downloads-device dbi-roblox-rank-cascader-select"
                dropdownBoxClassName="dbi-roblox-rank-store-rank-dropdown"
                dropdownOverlayClassName="dbi-roblox-rank-store-rank-overlay"
                options={rankCascaderOptions}
                value={rankCascaderValue}
                language={rankCascaderLanguage}
                resolveSecondLevelTitle={resolveRankSecondLevelTitle}
                onConfirm={(values) => {
                  setFormValues((prev) => ({
                    ...prev,
                    sources: cascaderValuesToSources(values),
                  }));
                }}
              />
            </Form.Item>

            <Form.Item
              className="form-item--align-middle"
              label={formatMessage({ id: 'dbi_label_time_settings' })}
              showStatusIcon={false}
            >
              <div className="search-item-date">
                <RangePicker
                  type="date"
                  className="search-border"
                  overlayClassName="dbi-high-layer"
                  typeSelectOverlayClassName="dbi-high-layer"
                  value={datePickerValue}
                  showSelectType={false}
                  language={locale === 'zh-CN' ? 'cn' : 'en'}
                  onChange={(nextValue: [moment.Moment, moment.Moment]) => {
                    const [startDate, endDate] = nextValue;
                    setFormValues((prev) => ({
                      ...prev,
                      dateRange: [
                        startDate.format('YYYY-MM-DD'),
                        (endDate || startDate).format('YYYY-MM-DD'),
                      ],
                    }));
                  }}
                />
              </div>
            </Form.Item>

            <Form.Item
              label={formatMessage({ id: 'dbi_label_data_amount' })}
              showStatusIcon={false}
            >
              <Radio.Group
                value={formValues.data_method}
                onChange={(value) => {
                  setFormValues((prev) => ({
                    ...prev,
                    data_method: value as IRobloxGameRankDownloadFormValues['data_method'],
                  }));
                }}
              >
                <Radio name="selectAll">
                  {formatMessage({ id: 'dbi_export_all_data' })}
                </Radio>
                <Radio name="selectTop">
                  {formatMessage({ id: 'dbi_select_top' })}
                </Radio>
                <Radio
                  name="checkedLines"
                  disabled={!selectedGameIds.length}
                >
                  {formatMessage({ id: 'dbi_export_checked_lines' })}
                </Radio>
              </Radio.Group>
            </Form.Item>

            {formValues.data_method === 'selectTop' ? (
              <Form.Item
                label={formatMessage({ id: 'dbi_label_download_type' })}
                showStatusIcon={false}
              >
                <Radio.Group
                  value={formValues.select_top_type}
                  onChange={(value) => {
                    setFormValues((prev) => ({
                      ...prev,
                      select_top_type: value as IRobloxGameRankDownloadFormValues['select_top_type'],
                    }));
                  }}
                >
                  {DOWNLOAD_TYPE_OPTIONS.map((item) => (
                    <Radio key={item.value} name={item.value}>
                      <span className="dbi-download-type-option">
                        {formatMessage({ id: item.label })}
                        <TipContext
                          className="dbi-tip-icon dbi-download-type-option-tip"
                          AWidth={450}
                          tipText={formatMessage({ id: item.tip })}
                        />
                      </span>
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            ) : null}

            {formValues.data_method === 'selectTop' ? (
              <Form.Item
                label=""
                showStatusIcon={false}
                status={isBlur ? 'error' : 'validating'}
              >
                <InputNumber
                  field="data_amount"
                  value={formValues.data_amount}
                  total={total}
                  isAutoAdjustMaxTipWidth
                  onChange={(_field, value) => {
                    const valueNum = Number.isNaN(value) ? 0 : value;
                    if (valueNum < 1) {
                      setFormValues((prev) => ({ ...prev, data_amount: 0 }));
                      setIsBlur(true);
                      return;
                    }

                    setFormValues((prev) => ({ ...prev, data_amount: valueNum }));
                    setIsBlur(false);
                  }}
                />
              </Form.Item>
            ) : null}

            <Form.Item
              label={formatMessage({ id: 'dbi_label_file_format' })}
              showStatusIcon={false}
            >
              <Radio.Group
                value={formValues.file_format}
                onChange={(value) => {
                  setFormValues((prev) => ({
                    ...prev,
                    file_format: value as IRobloxGameRankDownloadFormValues['file_format'],
                  }));
                }}
              >
                <Radio name="xlsx">Excel</Radio>
                <Radio name="csv">CSV</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item label="   ">
              <Form.Text style={{ color: '#9f9f9f' }}>
                {formatMessage({ id: 'dbi_download_modal_stick_tips' })}
              </Form.Text>
            </Form.Item>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button type="primary" onClick={handleConfirm}>
            {formatMessage({ id: 'dbi_confirm' })}
          </Button>
          <Button onClick={() => setVisible(false)}>
            {formatMessage({ id: 'dbi_cancel' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RobloxGameRankDownload;
